import { AlertTriangle } from '@untitledui/icons';
import type { ComponentType, ReactNode } from 'react';

import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { cx } from '@/utils/cx';

import { EditPencil } from '../components/EditPencil';
import type { HighlightResolverResult } from './types';

interface ValueAreaProps {
  label: string;
  result: HighlightResolverResult;
  /**
   * Text to render when `result.value` is null/empty. Defaults to '—'
   * which feels generic and "unfinished"; per-card consumers should
   * pass informative text like "Never emailed" / "No phone numbers"
   * (Attio reference). Kept optional so existing callers keep
   * working until they're migrated.
   */
  emptyText?: string;
  /** Optional secondary line beneath the primary value (e.g. region). */
  subValue?: ReactNode;
}

function ValueArea({ label, result, emptyText, subValue }: ValueAreaProps) {
  if (result.isLoading) {
    return (
      <div aria-hidden='true' data-testid={`highlight-skeleton-${label}`}>
        <Skeleton variant='text' width='5rem' height='1rem' />
      </div>
    );
  }
  if (result.isError) {
    return (
      <div
        role='alert'
        aria-label={`We couldn't load ${label}`}
        className='flex items-center gap-1 text-[var(--color-text-error-primary)]'
      >
        <AlertTriangle className='h-4 w-4 shrink-0' aria-hidden='true' />
        <span className='sr-only'>We couldn&apos;t load {label}</span>
      </div>
    );
  }
  const { value } = result;
  const isEmpty = value === null || value === undefined || value === '';
  if (isEmpty) {
    return (
      <p className='text-md font-medium text-ellipsis overflow-hidden whitespace-nowrap text-[var(--color-text-tertiary)]'>
        {emptyText ?? '—'}
      </p>
    );
  }
  return (
    <div className='min-w-0'>
      <p className='text-md font-semibold text-ellipsis overflow-hidden whitespace-nowrap text-[var(--color-text-primary)]'>
        {value}
      </p>
      {subValue && (
        <p className='text-xs text-[var(--color-text-tertiary)] text-ellipsis overflow-hidden whitespace-nowrap'>
          {subValue}
        </p>
      )}
    </div>
  );
}

interface HighlightCardShellProps {
  label: string;
  result: HighlightResolverResult;
  /**
   * Content-specific icon rendered in the top-right corner (small,
   * muted). Each card type should pass an icon that matches the data
   * (envelope for email, phone receiver for phone, building for
   * company, calendar for meetings, etc.) — Attio uses these per-card
   * icons to differentiate cards visually instead of leaving them as
   * label-only rectangles.
   */
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: 'true' }>;
  /** Empty-state text (e.g. "Never emailed"); falls back to '—'. */
  emptyText?: string;
  /** Optional secondary value line (e.g. region under company name). */
  subValue?: ReactNode;
  /**
   * When set, the card becomes an interactive button — cursor pointer,
   * hover background, keyboard-focusable. The card's value should be
   * meaningful as a click target (open associated entity, copy value,
   * switch tab, etc.). When `null`/undefined the card stays a static
   * `<div>`. Set independently of `result.value`: the consumer decides
   * whether interactivity makes sense in the empty state too.
   */
  onClick?: (() => void) | null;
  /** aria-label override when the card is interactive. */
  actionLabel?: string;
  /**
   * When set, a hover-reveal Edit pencil renders beside the label; clicking it
   * invokes this (opens the inline "Update {label}" modal). Independent of
   * `onClick` — an editable card can still have a card-level action (copy /
   * open associated entity), preserved via an overlay trigger so the pencil
   * stays clickable.
   */
  onEdit?: (() => void) | null;
  /** aria-label for the edit pencil. Defaults to "Edit {label}". */
  editLabel?: string;
}

// Card proportions match the Attio reference overview: short cards
// (~80px tall), subdued top-left label, content icon top-right,
// semibold value beneath, optional secondary line, informative empty
// state when no value is available. When `onClick` is provided the
// card renders as a button with hover + focus affordances.
export function HighlightCardShell({
  label,
  result,
  icon: Icon,
  emptyText,
  subValue,
  onClick,
  actionLabel,
  onEdit,
  editLabel,
}: HighlightCardShellProps) {
  // Filled, borderless tiles (Attio/Linear style) rather than outlined white
  // boxes — outlined empty boxes read as "unfinished". A subtle fill + strong
  // type hierarchy (micro uppercase label over a bold value) makes each tile
  // feel intentional and dense even when the value is short.
  const baseClassName =
    'bg-[var(--color-bg-secondary)] rounded-xl px-3.5 py-3 text-left block w-full';
  const interactiveClassName =
    'cursor-pointer hover:bg-[var(--color-bg-tertiary)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring';

  const labelRow = (
    <div className='flex items-start justify-between gap-2 mb-1.5'>
      <div className='flex items-center gap-1 min-w-0'>
        <p className='text-[11px] font-medium uppercase tracking-wide text-[var(--color-text-tertiary)] truncate'>
          {label}
        </p>
        {onEdit && (
          <EditPencil onEdit={onEdit} label={editLabel ?? `Edit ${label}`} />
        )}
      </div>
      {Icon && (
        <Icon
          className='w-3.5 h-3.5 shrink-0 text-fg-quaternary'
          aria-hidden='true'
        />
      )}
    </div>
  );

  const valueRow = (
    <div className='min-h-6 flex items-center'>
      <ValueArea
        label={label}
        result={result}
        emptyText={emptyText}
        subValue={subValue}
      />
    </div>
  );

  // Editable: the card is a div (so the Edit pencil can be its own button).
  // When the card also has a click action, render it as an absolute-inset
  // overlay button beneath the content; the content layer is pointer-events-none
  // so clicks fall through to it, while the pencil opts back in to clicks.
  if (onEdit) {
    return (
      <div
        className={cx(
          baseClassName,
          'group relative',
          onClick &&
            'cursor-pointer hover:bg-[var(--color-bg-tertiary)] transition-colors'
        )}
      >
        {onClick && (
          <button
            type='button'
            onClick={onClick}
            aria-label={actionLabel ?? label}
            className='absolute inset-0 z-0 rounded-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring'
          />
        )}
        <div className='pointer-events-none relative'>
          {labelRow}
          {valueRow}
        </div>
      </div>
    );
  }

  if (onClick) {
    return (
      <button
        type='button'
        onClick={onClick}
        aria-label={actionLabel ?? label}
        className={`${baseClassName} ${interactiveClassName}`}
      >
        {labelRow}
        {valueRow}
      </button>
    );
  }

  return (
    <div className={baseClassName}>
      {labelRow}
      {valueRow}
    </div>
  );
}
